import axios from "axios";

/**
 * Determina la base URL del backend.
 *
 * Regola: se `REACT_APP_BACKEND_URL` punta a un origin diverso dal sito
 * corrente, usa l'origin del sito stesso (URL relativi same-origin).
 * Motivo: in produzione il bundle JS viene servito da `https://sagigrp.com`
 * ma `REACT_APP_BACKEND_URL` può ancora essere l'URL Emergent
 * (`*.emergent.host`). Chiamando cross-origin il browser fa preflight e —
 * dato che l'ingress upstream restituisce `Allow-Origin: *` con
 * `Allow-Credentials: true` (combinazione vietata dalla spec CORS) —
 * BLOCCA TUTTE le richieste con `withCredentials: true`. Sintomo: ogni POST
 * fallisce con `net::ERR_FAILED 200 (OK)` e il form demo mostra
 * "Network error".
 *
 * Usando lo stesso origin del frontend, l'ingress instrada `/api/*` allo
 * stesso backend senza preflight → tutto funziona.
 */
function resolveBackendUrl() {
  const configured = process.env.REACT_APP_BACKEND_URL || "";
  if (typeof window === "undefined") return configured;
  try {
    const cu = new URL(configured);
    if (cu.origin !== window.location.origin) {
      return window.location.origin;
    }
  } catch { /* configured non è un URL valido → cade al return sotto */ }
  return configured || window.location.origin;
}

const BACKEND_URL = resolveBackendUrl();
export const API = `${BACKEND_URL}/api`;

// Diagnostica deploy: in produzione il valore è iniettato a build-time.
if (typeof window !== "undefined") {
  // eslint-disable-next-line no-console
  console.info("[HACCP] API base:", API, "· origin:", window.location.origin);
}

// Storage key per il Bearer token fallback (iOS Safari ITP, in-app browser, ecc.)
// Quando il cookie httpOnly viene bloccato, il login restituisce il token nel
// body JSON e lo salviamo qui. axios lo aggiunge automaticamente come
// `Authorization: Bearer <token>` ad ogni richiesta.
const TOKEN_STORAGE_KEY = "haccp_auth_token";

export function setAuthToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_STORAGE_KEY, token);
    else localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch { /* ignore quota errors */ }
}

export function getAuthToken() {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY) || "";
  } catch {
    return "";
  }
}

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Inietta Bearer token (se presente) su ogni richiesta. Coesiste con i cookie:
// se entrambi sono presenti, il backend usa il cookie (preferito). Su iOS dove
// i cookie sono bloccati, il Bearer fa il lavoro.
api.interceptors.request.use((config) => {
  const t = getAuthToken();
  if (t && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${t}`;
  }
  // R60.60 — Multi-sede: propaga la sede attiva come header su tutte le call
  try {
    const sede = window.localStorage.getItem("haccp_sede_id");
    if (sede && !config.headers["X-Sede-Id"]) {
      config.headers["X-Sede-Id"] = sede;
    }
  } catch { /* noop */ }
  return config;
});

// Se ricevo 401 dal server (token scaduto/invalido), provo a rinnovarlo
// automaticamente via /auth/refresh prima di ritornare l'errore al chiamante.
// Esperienza utente: nessuna interruzione visibile finché il refresh_token
// (7gg) è valido. Solo se anche il refresh fallisce, l'utente viene
// considerato sloggato e deve riaccedere.
let _refreshPromise = null;

async function _doRefresh() {
  // Singleton refresh: se 2+ request 401 partono in parallelo, fanno 1 sola
  // chiamata /auth/refresh, attendono tutte la stessa Promise.
  if (_refreshPromise) return _refreshPromise;
  _refreshPromise = (async () => {
    try {
      const stored = getAuthToken();
      // Su iOS in-app browser il cookie refresh_token non viene mandato:
      // passiamo il refresh_token nel body se conservato. Per ora il backend
      // accetta cookie OR body, e noi salviamo solo l'access_token in
      // localStorage. Per attivare body-based refresh dovremmo salvare anche
      // il refresh_token. Per ora confidiamo nel cookie (works su browser
      // standard, iOS Safari standalone PWA, Android Chrome).
      const resp = await axios.post(
        `${API}/auth/refresh`,
        { refresh_token: stored ? "" : "" },
        { withCredentials: true, timeout: 10000 },
      );
      if (resp.data?.access_token) {
        setAuthToken(resp.data.access_token);
      }
      return true;
    } catch {
      return false;
    } finally {
      // Reset DOPO 500ms così richieste ravvicinate si appoggiano allo stesso
      // refresh, ma il prossimo "ciclo" di 401 può comunque retentare.
      setTimeout(() => { _refreshPromise = null; }, 500);
    }
  })();
  return _refreshPromise;
}

api.interceptors.response.use(
  (resp) => resp,
  async (err) => {
    const status = err.response?.status;
    const cfg = err.config || {};
    const url = cfg.url || "";

    // Non rinnoviamo su endpoint di auth pubblici (eviteremmo loop infinito).
    const isAuthEndpoint = (
      url.endsWith("/auth/me")
      || url.endsWith("/auth/login")
      || url.endsWith("/auth/refresh")
      || url.endsWith("/auth/logout")
      || url.endsWith("/auth/clear-session")
    );

    if (status === 401 && !isAuthEndpoint && !cfg.__isRetry) {
      // R34.60: 401 con "Sessione scaduta dopo aggiornamento applicazione" → logout immediato.
      // Non tentiamo refresh perché anche quello fallirebbe (token vecchi sono tutti invalidati).
      const detail = String(err.response?.data?.detail || "").toLowerCase();
      if (detail.includes("aggiornamento applicazione") || detail.includes("aggiornament")) {
        setAuthToken(null);
        try {
          sessionStorage.setItem("haccp_logout_reason", "🔄 Abbiamo aggiornato l'applicazione. Accedi di nuovo per vedere le novità.");
        } catch { /* ignore */ }
        if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
          window.location.href = "/login?reason=app-updated";
        }
        return Promise.reject(err);
      }

      // Tenta refresh + retry una volta sola
      const ok = await _doRefresh();
      if (ok) {
        cfg.__isRetry = true;
        // Aggiorna l'header Authorization con il nuovo Bearer (se l'avevamo)
        const t = getAuthToken();
        if (t) cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${t}` };
        return api(cfg);
      }
      // Refresh fallito → pulisci bearer locale così la pagina ripara da sola
      setAuthToken(null);
    } else if (status === 401 && !isAuthEndpoint) {
      // Era già un retry e ha rifallito → pulisci.
      setAuthToken(null);
    }

    // R32: 403 con "sospes" / "Account sospeso" / "Azienda sospesa" → logout forzato.
    // L'utente era loggato ma il SA ha sospeso l'azienda/utente mentre era attivo.
    // Senza logout, la sessione resta orfana finché lui non ricarica.
    if (status === 403 && !isAuthEndpoint) {
      const detail = String(err.response?.data?.detail || "").toLowerCase();
      if (detail.includes("sospes") || detail.includes("scadut")) {
        setAuthToken(null);
        try {
          await api.post("/auth/logout").catch(() => {});
        } catch { /* ignore */ }
        // Memorizza ragione per mostrarla sulla pagina di login
        try { sessionStorage.setItem("haccp_logout_reason", err.response.data.detail); } catch { /* ignore */ }
        // Reindirizza a login con flag (se non già lì)
        if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
          window.location.href = "/login?reason=suspended";
        }
      }
    }

    return Promise.reject(err);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Si è verificato un errore. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
